/**
 * Portfolio image size sets — the SINGLE source of truth for which web variants
 * exist per orientation. Adding a new size = add one entry here; the resize Lambda
 * and the admin upload slots both read this. No schema or CDK change needed.
 *
 * `longEdge` is the target pixels on the image's LONG edge (height for portrait,
 * width for landscape). `full` has no longEdge — it is the largest web variant,
 * kept at its uploaded dimensions (never the RAW).
 */
export interface SizeSpec {
  label: string;
  longEdge?: number; // omitted for 'full'
}

export const SIZE_SETS: Record<'portrait' | 'landscape', SizeSpec[]> = {
  portrait: [
    { label: 'full' },
    { label: '4k',   longEdge: 3840 },
    { label: '2150', longEdge: 2150 },
    { label: '600',  longEdge: 600 },
  ],
  landscape: [
    { label: 'full' },
    { label: '2160', longEdge: 2160 },
    { label: '1400', longEdge: 1400 },
    { label: '900',  longEdge: 900 },
  ],
};

/** Resolve orientation from pixel dimensions. Square defaults to landscape. */
export function orientationOf(width: number, height: number): 'portrait' | 'landscape' {
  return height > width ? 'portrait' : 'landscape';
}

/** All valid variant labels across both sets (for validation). */
export const ALL_LABELS = Array.from(
  new Set([...SIZE_SETS.portrait, ...SIZE_SETS.landscape].map(s => s.label))
);

/** Web output formats. JPG is the universal <picture> fallback. */
export type VariantFormat = 'avif' | 'webp' | 'jpg';
export const ALL_FORMATS: VariantFormat[] = ['avif', 'webp', 'jpg'];

/**
 * Which formats each label gets during DEFAULT (automatic) generation.
 * The 'full' size is JPG-only by default — its full-resolution AVIF/WebP are
 * expensive and generated ON DEMAND only (the `fullFormats` flag). Every sized
 * label gets all three. This is the single rule both the resize Lambda and the
 * admin status UI read, so "complete" means the same thing on both sides.
 */
export function defaultFormatsFor(label: string): VariantFormat[] {
  return label === 'full' ? ['jpg'] : ['avif', 'webp', 'jpg'];
}

/**
 * The exact set of (label, format) pairs that MUST exist for an image of the
 * given orientation to be considered fully generated under default rules.
 * On-demand extras (e.g. full.avif) are NOT in this set, so they never block or
 * inflate completeness. Returned as "label:format" keys.
 */
export function expectedVariantKeys(orientation: 'portrait' | 'landscape'): string[] {
  const keys: string[] = [];
  for (const { label } of SIZE_SETS[orientation]) {
    for (const fmt of defaultFormatsFor(label)) keys.push(`${label}:${fmt}`);
  }
  return keys;
}

/** Normalize a format string to a canonical VariantFormat key (jpeg → jpg). */
export function normalizeFormat(fmt: string): string {
  return String(fmt || '').toLowerCase().replace('jpeg', 'jpg');
}
